import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Dumbbell, 
  Users, 
  CreditCard,
  Crown,
  Menu,
  Building2,
  X,
  ChevronDown,
  Dumbbell as TrainerIcon,
  UserPlus,
  LogOut
} from 'lucide-react';

import logo from '../../assets/logo.png';
import { authService } from '../../services/authService';
import { showToast } from '../Toast';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navItems: NavItem[] = [
    { label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/dashboard' },
    { label: 'Programs', icon: <Dumbbell size={20} />, path: '/programs' },
    { label: 'Gym Management', icon: <Building2 size={20} />, path: '/gyms' },

    { 
      label: 'User management', 
      icon: <Users size={20} />,
      children: [
        { label: 'Trainers', icon: <TrainerIcon size={20} />, path: '/trainers' },
        { label: 'Members', icon: <UserPlus size={20} />, path: '/members' },
        // { label: 'Verifications', icon: <UserCheck size={20} />, path: '/verifications' },
      ]
    },
    { 
      label: 'Billing', 
      icon: <CreditCard size={20} />,
      children: [
        { label: 'Billing', icon: <CreditCard size={20} />, path: '/billing' },
        { label: 'Subscription Plans', icon: <Crown size={20} />, path: '/subscription/plans' },
      ]
    },
  ];

  const isActive = (path?: string) => path ? location.pathname === path : false;

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await authService.logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      showToast('Logout failed', 'error');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-[#16251D] border-r border-gray-800 z-50
          transition-transform duration-300 ease-in-out
          ${isCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
          w-64 flex flex-col
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center space-x-2 flex-1">
            <img 
              src={logo} 
              alt="Gymnet Logo" 
              className="h-10 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.children ? (
                // Dropdown item
                <>
                  <button
                    onClick={() => toggleExpanded(item.label)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-lg
                      transition-colors duration-200
                      ${
                        expandedItems.includes(item.label)
                          ? 'bg-[#FFFFFF] bg-opacity-10 text-[#FFFFFF] '
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${
                        expandedItems.includes(item.label) ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {/* Dropdown children */}
                  {expandedItems.includes(item.label) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path!}
                          className={`
                            flex items-center space-x-3 px-4 py-2 rounded-lg
                            transition-colors duration-200 text-sm
                            ${
                              isActive(child.path)
                                ? 'bg-[#23F47C] bg-opacity-10 text-[#23F47C] border border-[#23F47C] border-opacity-30'
                                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                            }
                          `}
                        >
                          {child.icon}
                          <span className="font-medium">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Regular link item
                <Link
                  to={item.path!}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-colors duration-200
                    ${
                      isActive(item.path)
                        ? 'bg-[#23F47C] bg-opacity-10 text-[#23F47C] border border-[#23F47C] border-opacity-30'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }
                  `}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User Profile Section - Fixed at Bottom */}
        <div className="mt-auto p-4 border-t border-gray-800">
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-gray-800 transition-colors text-left group"
          >
            <div className="w-10 h-10 rounded-full bg-[#23F47C] bg-opacity-20 flex items-center justify-center flex-shrink-0">
              <span className="text-[#23F47C] font-semibold text-sm">A</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">Admin User</p>
              <p className="text-xs text-gray-400 truncate">Administrator</p>
            </div>
            <LogOut size={16} className="text-gray-400 group-hover:text-[#23F47C] transition-colors" />
          </button>
        </div>

        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-[#16251D] border border-gray-800 rounded-lg p-6 max-w-sm mx-4 shadow-lg">
              <h3 className="text-lg font-bold text-white mb-2">Logout</h3>
              <p className="text-gray-400 mb-6">Are you sure you want to logout?</p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  disabled={isLoggingOut}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoggingOut ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Logging out...
                    </>
                  ) : (
                    <>
                      <LogOut size={16} />
                      Logout
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile menu button */}
      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-30 lg:hidden bg-gray-900 p-2 rounded-lg text-gray-400 hover:text-white"
      >
        <Menu size={24} />
      </button>
    </>
  );
};

export default Sidebar;
